import {
  NewStatus,
  NewStatusSet,
  Status,
  StatusDTO,
  StatusId,
  StatusOrderResponse,
  StatusSet,
  StatusSetDTO,
  UpdateStatusObject,
  UpdateStatusSetObject,
} from "../models/statusSets";
import { AssetsPaginatedResponse, CursorState, ProjectId } from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

export const getStatusSetClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => {
  const base = http.getAssetsServiceBasePath(projectId);

  return {
    // `includeDeleted=true` reconciled from apps/acs-assets/src/clients/getStatusSetsAPIClient.ts
    // (issue #2500). Added unconditionally rather than behind an opt-in flag: none of
    // shared-web's callers of this client (AssetsPlugin, SystemsPlugin) invoke `get` at all
    // (they only use `list`/`batchGetStatuses`), so there's no existing no-param behavior to
    // preserve, and the param is additive/low-risk for any future caller.
    get: (id: StatusSet["id"]): Promise<StatusSetDTO> =>
      http.get<StatusSetDTO>(
        `${base}/${Endpoints.StatusSets}/${id}?includeDeleted=true`
      ),

    getDefault: (): Promise<StatusSetDTO> =>
      http.get<StatusSetDTO>(`${base}/${Endpoints.StatusSets}:default`),

    list: (
      cursorState?: CursorState
    ): Promise<AssetsPaginatedResponse<StatusSetDTO[]>> => {
      const searchString = buildSearchString({
        cursorState,
        includeDeleted: true,
      });

      return http.get<AssetsPaginatedResponse<StatusSetDTO[]>>(
        `${base}/${Endpoints.StatusSets}${
          searchString ? `?${searchString}` : ""
        }`
      );
    },

    batchGetById: async (ids: StatusSet["id"][]): Promise<StatusSetDTO[]> => {
      const resp = await http.post<{ results: StatusSetDTO[] }>(
        `${base}/${Endpoints.StatusSets}:batch-get`,
        { body: JSON.stringify({ ids }) }
      );
      return resp.results;
    },

    batchGetStatuses: async (ids: Status["id"][]): Promise<StatusDTO[]> => {
      const resp = await http.post<{ results: StatusDTO[] }>(
        `${base}/${Endpoints.Statuses}:batch-get`,
        { body: JSON.stringify({ ids }) }
      );
      return resp.results;
    },

    create: (
      statusSet: NewStatusSet,
      renameConflicting?: boolean
    ): Promise<StatusSetDTO> => {
      const searchString = buildSearchString({ renameConflicting });

      return http.post<StatusSetDTO>(
        `${base}/${Endpoints.StatusSets}${
          searchString ? `?${searchString}` : ""
        }`,
        { body: JSON.stringify(statusSet) }
      );
    },

    // `includeDeleted=true` — see reconciliation note on `get` above.
    update: ({
      id,
      ...statusSet
    }: Pick<StatusSet, "id"> &
      UpdateStatusSetObject): Promise<StatusSetDTO> =>
      http.patch<StatusSetDTO>(
        `${base}/${Endpoints.StatusSets}/${id}?includeDeleted=true`,
        { body: JSON.stringify(statusSet) }
      ),

    // `includeDeleted=true` — see reconciliation note on `get` above.
    delete: (id: StatusSet["id"]): Promise<StatusSetDTO> =>
      http.del<StatusSetDTO>(
        `${base}/${Endpoints.StatusSets}/${id}?includeDeleted=true`
      ),

    // `includeDeleted=true` — see reconciliation note on `get` above.
    undelete: (id: StatusSet["id"]): Promise<StatusSetDTO> =>
      http.post<StatusSetDTO>(
        `${base}/${Endpoints.StatusSets}/${id}/undelete?includeDeleted=true`
      ),

    createStatus: (status: NewStatus): Promise<StatusDTO> =>
      http.post<StatusDTO>(`${base}/${Endpoints.Statuses}`, {
        body: JSON.stringify(status),
      }),

    updateStatus: ({
      id,
      ...status
    }: Pick<Status, "id"> & UpdateStatusObject): Promise<StatusDTO> =>
      http.patch<StatusDTO>(`${base}/${Endpoints.Statuses}/${id}`, {
        body: JSON.stringify(status),
      }),

    deleteStatus: (id: StatusId): Promise<StatusDTO> =>
      http.del<StatusDTO>(`${base}/${Endpoints.Statuses}/${id}`),

    undeleteStatus: (id: StatusId): Promise<StatusDTO> =>
      http.post<StatusDTO>(`${base}/${Endpoints.Statuses}/${id}/undelete`),

    // `includeDeleted=true` — see reconciliation note on `get` above.
    setOrder: (
      statusSetId: StatusSet["id"],
      assetStatusIds: StatusId[]
    ): Promise<StatusOrderResponse> =>
      http.put<StatusOrderResponse>(
        `${base}/${
          Endpoints.StatusSets
        }/${statusSetId}/order?includeDeleted=true`,
        { body: JSON.stringify({ assetStatusIds }) }
      ),
  };
};

/**
 * The shape returned by {@link getStatusSetClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getStatusSetClient>`.
 */
export type StatusSetClient = ReturnType<typeof getStatusSetClient>;
